'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import Image from 'next/image';

import { cn } from '@/app/utils/cn';
import { adaptCategories } from '@/app/utils/blogAdapter';

import Author from '@/components/shared/author';
import SocialMediaShare from '@/components/shared/socialMediaShare';
import { ImagePlacerholder } from '@/components/shared/dotCardImage';
import LinkNoPrefetch from '@/components/link-noprefetch';
import TagList from '../../TagList';
import { getDotcmsImageURL } from '@/app/utils/getDotcmsImageURL';

const HeaderImage = ({ image, alt }) => {
    const cssClasses = cn('w-auto rounded-lg aspect-video object-cover');
    return (
        <>
            {image ? (
                <Image
                    src={getDotcmsImageURL({ src: image.identifier })}
                    alt={alt || 'dotCMS Site Image'}
                    priority
                    quality={100}
                    width={692}
                    height={390}
                    sizes="(max-width: 692px) calc(100vw - 32px), 692px"
                    className={cssClasses}
                />
            ) : (
                <div className={cssClasses}>
                    <ImagePlacerholder placeholderFontSize="5xl" />
                </div>
            )}
        </>
    );
};

const TopicsCovered = ({ categories, hrefMode, tags }) => {
    const items = tags || categories;

    if (!items || !Array.isArray(items) || items.length === 0) {
        return null;
    }

    return (
        <div className="hidden flex-1 flex-col gap-2 md:flex">
            <p className="text-blue-600">Topics Covered:</p>
            <div className="flex flex-wrap gap-2">
                {items.map(({ key, value, text }) => {
                    const displayText = text || value;
                    const uniqueKey = key || text;

                    return hrefMode ? (
                        <LinkNoPrefetch
                            key={uniqueKey}
                            href={`/blog?${categories ? 'category' : 'tag'}=${displayText}`}
                            className="rounded-[0.250rem] bg-white px-3 py-1 text-sm
                            text-fuschia-800">
                            {displayText}
                        </LinkNoPrefetch>
                    ) : (
                        <p
                            key={uniqueKey}
                            className="rounded-[0.250rem] bg-white px-3 py-1 text-sm text-fuschia-800">
                            {displayText}
                        </p>
                    );
                })}
            </div>
        </div>
    );
};

export const DetailHeader = ({
    publishDate,
    updatedDate,
    tags = [],
    image,
    title,
    imageCredit,
    categories,
    authors,
    thumbnailAlt,
    notShowDate
}) => {
    const pathname = usePathname();
    const [url, setUrl] = useState('');
    const adaptedCategories = adaptCategories(categories);

    useEffect(() => {
        const urlString = new URL(pathname, window.location.origin);
        setUrl(urlString);
    }, [pathname]);

    return (
        <div className="background-crossline p-4 pt-8 lg:p-16 lg:px-32">
            <div className="container flex flex-col gap-6 p-0 lg:gap-9">
                <div className="flex flex-col gap-6 lg:w-2/3 ">
                    {!!(adaptedCategories || []).length && (
                        <TagList hrefMode categories={adaptedCategories} />
                    )}
                    <h1>{title}</h1>
                </div>
                <div className="flex flex-col gap-8 lg:flex-row">
                    <div className="flex flex-col gap-2 lg:w-2/3">
                        <HeaderImage image={image} alt={thumbnailAlt || title} />
                        {imageCredit && (
                            <p className="text-sm text-blue-600">Image Credit: {imageCredit}</p>
                        )}
                    </div>
                    <div className="flex flex-col gap-6 lg:w-1/3 lg:gap-8">
                        <div className="flex flex-col gap-4 lg:gap-8">
                            {!notShowDate && (
                                <div className="flex flex-col gap-1">
                                    <time className="text-sm text-blue-600">
                                        <strong>Published on</strong> {publishDate}
                                    </time>
                                    {updatedDate && (
                                        <time className="text-sm text-blue-600">
                                            <strong>Last modified on</strong> {updatedDate}
                                        </time>
                                    )}
                                </div>
                            )}
                            {authors?.length > 0 &&
                                authors.map((item, index) => (
                                    <Author
                                        key={
                                            item?.identifier ||
                                            `author-${item?.firstName || ''}-${item?.lastName || ''}-${index}`
                                        }
                                        author={item}
                                    />
                                ))}
                        </div>
                        {!!tags.length && <TopicsCovered tags={tags} />}
                        <div
                            className={cn(
                                'flex justify-between items-center',
                                { 'mb-6': !!imageCredit },
                                { 'mt-auto': !!categories?.length || !!authors?.[0] }
                            )}>
                            <p className="flex text-sm text-blue-700">Share this article on:</p>
                            {url && <SocialMediaShare url={url} />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
